const getApiBase = () => {
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  if (base.endsWith("/admin")) {
    return `${base}/mini-game`;
  }
  return `${base}/admin/mini-game`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const handleResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status === false) {
    const errorMsg = data?.message || data?.error || `Request failed with status ${response.status}`;
    const err = new Error(errorMsg);
    err.response = data;
    throw err;
  }
  return data;
};

/**
 * Tap Game and Boom Cards are the same admin surface (settings + paginated
 * history) at two different backend path segments — `tap` / `boomcards` —
 * see px's src/admin/mini-game module. One factory avoids writing the same
 * three calls out twice.
 */
const makeMiniGameApi = (segment) => ({
  fetchSettings: async () => {
    const res = await fetch(`${getApiBase()}/${segment}/settings`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },

  updateSettings: async (payload) => {
    const res = await fetch(`${getApiBase()}/${segment}/settings`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(res);
  },

  /**
   * Daily play counts and points for the trend charts.
   * `days` accepts 7, 14 or 30; the backend falls back to 7 for anything else.
   */
  fetchTrends: async (days = 7) => {
    const res = await fetch(`${getApiBase()}/${segment}/trends?days=${days}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
  /**
   * Paged session history. `eventId` narrows to one event; `overlap` to only
   * sessions played while another game's session was open. The response also
   * carries `summary`, totals for everything the filters match.
   */
  fetchHistory: async ({ page = 1, limit = 20, search = "", status = "", eventId = "", overlap = false } = {}) => {
    const query = new URLSearchParams();
    query.append("page", page);
    query.append("limit", limit);
    if (search) query.append("search", search.trim());
    if (status && status !== "all") query.append("status", status);
    if (eventId) query.append("eventId", eventId);
    if (overlap) query.append("overlap", "1");

    const res = await fetch(`${getApiBase()}/${segment}/history?${query.toString()}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },

  /** Events the history can be filtered to, newest first. */
  fetchHistoryEvents: async () => {
    const res = await fetch(`${getApiBase()}/${segment}/history/events`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    return handleResponse(res);
  },
});

export const tapGameApi = makeMiniGameApi("tap");
export const boomCardsApi = makeMiniGameApi("boomcards");
// The backend path segment is "2048"; the stored gameKey is "game_2048".
export const game2048Api = makeMiniGameApi("2048");
// Fruit Cut uses the same formula settings and admin contract as 2048.
export const fruitCutApi = makeMiniGameApi("fruit-cut");
// The Slot Machine. Path segment is "slots"; the stored gameKey is "slot_machine".
export const slotsGameApi = makeMiniGameApi("slots");
// The Lucky Spin Wheel. Path segment is "spinwheel"; stored gameKey is "spin_win".
export const spinWheelApi = makeMiniGameApi("spinwheel");
